/**
 * AllowanceTypeDetailModal — View allowance type details (simple, no sub-resources).
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Hash,
  Type,
  FileText,
  CheckCircle,
  XCircle,
  Receipt,
  Repeat,
  Tag,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useDeleteAllowanceType } from '../hooks';
import { triggerHapticFeedback } from '../utils/adminAnimations';
import toast from 'react-hot-toast';
import type { AllowanceType } from '../types';

interface AllowanceTypeDetailModalProps {
  allowanceType: AllowanceType;
  onClose: () => void;
  onEdit?: (allowanceType: AllowanceType) => void;
}

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 w-5 h-5 mt-0.5 text-slate-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-neutral-500">{label}</p>
        <p className="text-sm font-medium text-neutral-800 truncate">{value || '\u2014'}</p>
      </div>
    </div>
  );
}

export function AllowanceTypeDetailModal({ allowanceType, onClose, onEdit }: AllowanceTypeDetailModalProps) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteAllowanceType();
  const { openConfirm } = useModal();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={allowanceType.name}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={{ backgroundColor: MODAL_COLORS.interactive, border: `1px solid ${MODAL_COLORS.border}` }}>
          <div className="flex items-center gap-2">
            {allowanceType.isActive ? (
              <CheckCircle className="w-4 h-4 opacity-60" />
            ) : (
              <XCircle className="w-4 h-4 opacity-60" />
            )}
            <span className="text-sm font-bold">{t('allowanceTypes.status')}</span>
            <span
              className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
              style={{
                backgroundColor: allowanceType.isActive ? '#ecfdf5' : '#fee2e2',
                border: '1px solid rgba(148, 163, 184, 0.3)',
              }}
            >
              {allowanceType.isActive ? t('allowanceTypes.active') : t('allowanceTypes.inactive')}
            </span>
          </div>
        </div>

        {/* Details */}
        <div
          className="glass-surface rounded-2xl p-3"
          style={{ border: `1px solid ${MODAL_COLORS.border}` }}
        >
          <InfoRow icon={<Hash className="w-4 h-4" />} label={t('allowanceTypes.code')} value={allowanceType.code} />
          <InfoRow icon={<Type className="w-4 h-4" />} label={t('allowanceTypes.name')} value={allowanceType.name} />
          <InfoRow icon={<Type className="w-4 h-4" />} label={t('allowanceTypes.nameEn')} value={allowanceType.nameEn} />
          <InfoRow icon={<FileText className="w-4 h-4" />} label={t('allowanceTypes.description')} value={allowanceType.description} />
          <InfoRow icon={<Tag className="w-4 h-4" />} label={t('allowanceTypes.category')} value={allowanceType.category} />
          <InfoRow
            icon={<Receipt className="w-4 h-4" />}
            label={t('allowanceTypes.isTaxable')}
            value={
              <span
                className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                style={{
                  backgroundColor: allowanceType.isTaxable ? '#fff3e0' : '#f3f4f6',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                }}
              >
                {allowanceType.isTaxable ? t('common.yes') : t('common.no')}
              </span>
            }
          />
          <InfoRow
            icon={<Repeat className="w-4 h-4" />}
            label={t('allowanceTypes.isRecurring')}
            value={
              <span
                className="inline-block px-2 py-0.5 rounded-lg text-xs font-semibold"
                style={{
                  backgroundColor: allowanceType.isRecurring ? '#e3f2fd' : '#f3f4f6',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                }}
              >
                {allowanceType.isRecurring ? t('common.yes') : t('common.no')}
              </span>
            }
          />
        </div>

        {/* Edit */}
        {onEdit && (
          <button
            onClick={() => {
              triggerHapticFeedback();
              onEdit(allowanceType);
              requestClose();
            }}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer hover:translate-x-[1px] hover:translate-y-[1px]"
            style={{
              backgroundColor: MODAL_COLORS.interactive,
              border: `1px solid ${MODAL_COLORS.border}`,
            }}
          >
            {t('allowanceTypes.edit')}
          </button>
        )}

        {/* Delete */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('allowanceTypes.deleteTitle'),
              message: t('allowanceTypes.deleteMessage'),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: async () => {
                try {
                  await deleteMutation.mutateAsync(allowanceType.id);
                  toast.success(t('allowanceTypes.deleteSuccess'));
                  requestClose();
                } catch {
                  toast.error(t('allowanceTypes.deleteError'));
                }
              },
            });
          }}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer"
          style={{
            backgroundColor: 'rgba(254, 226, 226, 0.6)',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#B91C1C',
          }}
        >
          <Trash2 className="w-4 h-4" />
          {t('common.delete')}
        </button>
      </div>
    </FormModal>
  );
}
